import React, { useRef } from 'react';
import { Star, Sparkles, Download, Palette, BookText, Lightbulb, Info, AlertCircle } from 'lucide-react';
import { jsPDF } from 'jspdf';
import { Button } from '@/components/ui/button';
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardFooter, 
  CardHeader, 
  CardTitle 
} from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { useToast } from '@/hooks/use-toast';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import ShareToGallerySection from './ShareToGallerySection';

export interface AnalysisResult {
  overallRating: number;
  detailedRatings: {
    marketability: number;
    subjectClarity?: number;
    genreClarity?: number;
    typography: number;
    professionalAppearance: number;
    composition: number;
    colorScheme: number;
  };
  bookType?: "fiction" | "nonfiction";
  genrePrediction: string;
  technicalIssues?: {
    textSpacing: "PASS" | "FAIL";
    typographyBalance: "PASS" | "FAIL";
    colorContrast: "PASS" | "FAIL";
  };
  titleFeedback?: string;
  critique: string;
  suggestions: string[];
}

interface ResultsSectionProps {
  result: AnalysisResult | null;
  imageUrl: string | null;
}

const ResultsSection: React.FC<ResultsSectionProps> = ({ result, imageUrl }) => {
  const { toast } = useToast();
  const analysisRef = useRef<HTMLDivElement>(null);

  if (!result) return null;

  const handleDownload = () => {
    if (!result) return;
    
    try {
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 15;
      let y = 20;

      // Add logo at the top
      const logo = new Image();
      logo.src = '/diy-logo.png';
      doc.addImage(logo, 'PNG', margin, y, 50, 10);
      y += 20;

      // Title
      doc.setFontSize(20);
      doc.setFont('helvetica', 'bold');
      doc.text('Book Cover Analysis', margin, y);
      y += 15;

      // Overall Rating
      doc.setFontSize(14);
      doc.text(`Overall Rating: ${result.overallRating}/10`, margin, y);
      y += 10;

      // Genre
      doc.setFontSize(12);
      doc.setFont('helvetica', 'normal');
      doc.text(`Genre: ${result.genrePrediction}`, margin, y);
      y += 15;

      // Technical Issues
      if (result.technicalIssues) {
        doc.setFont('helvetica', 'bold');
        doc.text('Technical Issues:', margin, y);
        y += 8;
        doc.setFont('helvetica', 'normal');
        
        Object.entries(result.technicalIssues).forEach(([key, value]) => {
          if (typeof value === 'string') {
            const status = value === 'PASS' ? '✓ PASS' : '✗ FAIL';
            doc.text(`${key}: ${status}`, margin + 5, y);
            y += 6;
          }
        });
        y += 10;
      }

      // Title Feedback
      if (result.titleFeedback) {
        doc.setFont('helvetica', 'bold');
        doc.text('Title Feedback:', margin, y);
        y += 8;
        doc.setFont('helvetica', 'normal');
        const feedbackLines = doc.splitTextToSize(result.titleFeedback, pageWidth - 2 * margin);
        doc.text(feedbackLines, margin + 5, y);
        y += feedbackLines.length * 6 + 10;
      }

      // Detailed Ratings
      doc.setFont('helvetica', 'bold');
      doc.text('Detailed Ratings:', margin, y);
      y += 8;
      doc.setFont('helvetica', 'normal');
      
      Object.entries(result.detailedRatings).forEach(([key, value]) => {
        if (value !== undefined) {
          const label = key.replace(/([A-Z])/g, ' $1').trim();
          doc.text(`${label}: ${value}/10`, margin + 5, y);
          y += 6;
        }
      });
      y += 10;

      // Check if we need a new page
      if (y > 250) {
        doc.addPage();
        y = 20;
      }

      // Critique
      doc.setFont('helvetica', 'bold');
      doc.text('Critique:', margin, y);
      y += 8;
      doc.setFont('helvetica', 'normal');
      const critiqueLines = doc.splitTextToSize(result.critique, pageWidth - 2 * margin);
      doc.text(critiqueLines, margin + 5, y);
      y += critiqueLines.length * 6 + 10;

      // Check if we need a new page for suggestions
      if (y > 230) {
        doc.addPage();
        y = 20;
      }

      // Suggestions
      doc.setFont('helvetica', 'bold');
      doc.text('Suggestions for Improvement:', margin, y);
      y += 8;
      doc.setFont('helvetica', 'normal');
      
      result.suggestions.forEach((suggestion, index) => {
        const suggestionLines = doc.splitTextToSize(`${index + 1}. ${suggestion}`, pageWidth - 2 * margin - 5);
        
        // Check if we need a new page
        if (y + suggestionLines.length * 6 > 280) {
          doc.addPage();
          y = 20;
        }
        
        doc.text(suggestionLines, margin + 5, y);
        y += suggestionLines.length * 6 + 5;
      });

      // Add footer with website link on all pages
      const totalPages = doc.internal.pages.length - 1;
      for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        doc.setFontSize(10);
        doc.setFont('helvetica', 'italic');
        doc.setTextColor(100, 100, 100);
        doc.text('www.diybookcovers.com', pageWidth / 2, pageHeight - 10, { align: 'center' });
        doc.setTextColor(0, 0, 0);
      }

      doc.save(`book-cover-analysis-${Date.now()}.pdf`);
      
      toast({
        title: "Analysis Downloaded",
        description: "Your cover critique has been saved as PDF",
      });
    } catch (error) {
      console.error('PDF generation error:', error);
      toast({
        title: "Download Failed",
        description: "There was an error generating the PDF. Please try again.",
        variant: "destructive"
      });
    }
  };

  const getRatingColor = (rating: number) => {
    if (rating >= 8) return 'text-green-500';
    if (rating >= 5) return 'text-amber-500';
    return 'text-red-500';
  };

  const getIssueStatusColor = (status: "PASS" | "FAIL") => {
    return status === "PASS" ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800';
  };

  const failedTechnicalIssues = result.technicalIssues ? 
    Object.entries(result.technicalIssues).filter(([_, status]) => status === "FAIL") : 
    [];

  const renderRatingStars = (rating: number, max: number = 10) => {
    return (
      <div className="flex items-center">
        <div className="flex items-center">
          {[...Array(max)].map((_, i) => (
            <Star
              key={i}
              className={`h-4 w-4 transition-all ${
                i < rating ? 'text-bookGold fill-bookGold scale-110' : 'text-gray-300'
              }`}
            />
          ))}
        </div>
        <span className={`ml-2 font-medium ${getRatingColor(rating)}`}>{rating}/{max}</span>
      </div>
    );
  };

  return (
    <div className="w-full max-w-4xl mx-auto my-8 px-4 animate-fade-in">
      <div className="grid grid-cols-1 md:grid-cols-5 gap-8">
        <div className="md:col-span-2">
          {imageUrl && (
            <div className="book-cover-wrapper sticky top-8 transform hover:rotate-1 transition-all duration-500">
              <img
                src={imageUrl}
                alt="Analyzed book cover"
                className="w-full h-auto rounded-md shadow-xl"
              />
              <div className="absolute -bottom-3 -right-3 bg-gradient-to-r from-bookRed to-bookGold text-white text-xl font-bold rounded-full w-16 h-16 flex items-center justify-center transform rotate-12 shadow-lg">
                {result.overallRating}/10
              </div>
            </div>
          )}
        </div>
        
        <div className="md:col-span-3">
          <Card ref={analysisRef} className="shadow-xl border-bookNavy overflow-hidden bg-gradient-to-br from-white to-bookCream">
            <CardHeader className="bg-gradient-to-r from-bookNavy to-bookNavy/90 text-white rounded-t-lg">
              <CardTitle className="text-2xl flex items-center justify-between">
                <span className="flex items-center">
                  <Sparkles className="mr-2 h-5 w-5 text-bookGold" />
                  Cover Analysis
                </span>
                <span className="text-3xl font-bold bg-white text-bookNavy rounded-full w-12 h-12 flex items-center justify-center shadow-inner">
                  {result.overallRating}
                </span>
              </CardTitle>
              <CardDescription className="text-gray-200 mt-2">
                <div className="flex items-center bg-bookNavy/40 px-3 py-2 rounded-lg">
                  <BookText className="mr-2 h-4 w-4" />
                  <span className="flex items-center">
                    Looks like: <span className="font-medium ml-1">{result.bookType === "fiction" ? "Fiction / " : "Nonfiction / "}<strong className="text-bookGold">{result.genrePrediction}</strong></span>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger className="ml-1">
                          <Info className="h-4 w-4 text-gray-300" />
                        </TooltipTrigger>
                        <TooltipContent className="max-w-xs">
                          <p>Not your genre? Then your cover may be sending the wrong message to potential readers.</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </span>
                </div>
              </CardDescription>
            </CardHeader>
            
            <CardContent className="pt-6">
              <div className="space-y-6">
                {failedTechnicalIssues.length > 0 && (
                  <div className="mb-6">
                    <h3 className="text-lg font-serif font-semibold mb-3 flex items-center">
                      <AlertCircle className="mr-2 h-5 w-5 text-bookRed" />
                      Technical Issues
                    </h3>
                    <div className="grid grid-cols-1 gap-2 md:grid-cols-3">
                      {failedTechnicalIssues.map(([issue, status]) => (
                        <div key={issue} className={`text-sm rounded-md px-3 py-2 relative ${getIssueStatusColor(status)}`}>
                          <div className="font-medium capitalize">{issue.replace(/([A-Z])/g, ' $1').trim()}</div>
                          <div className="font-bold">{status}</div>
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger className="absolute inset-0 cursor-help" />
                              <TooltipContent className="max-w-xs p-3">
                                {issue === 'textSpacing' && 
                                  <p>Text elements are crowded or positioned too close to edges of the cover.</p>
                                }
                                {issue === 'typographyBalance' && 
                                  <p>Text elements aren't properly aligned or have inconsistent spacing from edges.</p>
                                }
                                {issue === 'colorContrast' && 
                                  <p>Text doesn't have sufficient contrast with background for optimal readability.</p>
                                }
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {result.titleFeedback && (
                  <div className="mb-6">
                    <h3 className="text-lg font-serif font-semibold mb-3 flex items-center">
                      <BookText className="mr-2 h-5 w-5 text-bookRed" />
                      Title Analysis
                    </h3>
                    <p className="text-gray-700 bg-gray-50 p-3 rounded-md italic">{result.titleFeedback}</p>
                  </div>
                )}

                <div>
                  <h3 className="text-lg font-serif font-semibold mb-3 flex items-center">
                    <Palette className="mr-2 h-5 w-5 text-bookRed" />
                    Detailed Ratings
                  </h3>
                  
                  {result.detailedRatings?.marketability && (
                    <div className="rating-item hover:bg-gray-50 transition-colors">
                      <span>Marketability</span>
                      {renderRatingStars(result.detailedRatings.marketability)}
                    </div>
                  )}
                  
                  {result.detailedRatings?.genreClarity && (
                    <div className="rating-item hover:bg-gray-50 transition-colors">
                      <span>Genre Clarity</span>
                      {renderRatingStars(result.detailedRatings.genreClarity)}
                    </div>
                  )}
                  
                  {result.detailedRatings?.subjectClarity && (
                    <div className="rating-item hover:bg-gray-50 transition-colors">
                      <span>Subject Clarity</span>
                      {renderRatingStars(result.detailedRatings.subjectClarity)}
                    </div>
                  )}
                  
                  {result.detailedRatings?.typography && (
                    <div className="rating-item hover:bg-gray-50 transition-colors">
                      <span>Typography</span>
                      {renderRatingStars(result.detailedRatings.typography)}
                    </div>
                  )}
                  
                  {result.detailedRatings?.professionalAppearance && (
                    <div className="rating-item hover:bg-gray-50 transition-colors">
                      <span>Professional Appearance</span>
                      {renderRatingStars(result.detailedRatings.professionalAppearance)}
                    </div>
                  )}
                  
                  {result.detailedRatings?.composition && (
                    <div className="rating-item hover:bg-gray-50 transition-colors">
                      <span>Composition</span>
                      {renderRatingStars(result.detailedRatings.composition)}
                    </div>
                  )}
                  
                  {result.detailedRatings?.colorScheme && (
                    <div className="rating-item hover:bg-gray-50 transition-colors">
                      <span>Color Scheme</span>
                      {renderRatingStars(result.detailedRatings.colorScheme)}
                    </div>
                  )}
                </div>
                
                <Separator className="bg-gradient-to-r from-transparent via-bookGold/30 to-transparent" />
                
                <div>
                  <h3 className="text-lg font-serif font-semibold mb-3 flex items-center">
                    <BookText className="mr-2 h-5 w-5 text-bookRed" />
                    Critique
                  </h3>
                  <p className="text-gray-700 whitespace-pre-line">{result.critique}</p>
                </div>
                
                <div>
                  <h3 className="text-lg font-serif font-semibold mb-3 flex items-center">
                    <Lightbulb className="mr-2 h-5 w-5 text-bookRed" />
                    Suggestions
                  </h3>
                  <ul className="list-disc pl-5 space-y-2">
                    {result.suggestions.map((suggestion, index) => (
                      <li key={index} className="text-gray-700 hover:text-bookNavy transition-colors">
                        {suggestion}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </CardContent>
            
            <CardFooter className="bg-gray-50 rounded-b-lg flex gap-2">
              <Button 
                asChild
                variant="outline" 
                className="flex-1 border-bookNavy hover:bg-bookNavy hover:text-white transition-all"
              >
                <a href="https://diybookcovers.com/covermaker/redesign" target="_blank" rel="noopener noreferrer">
                  <Sparkles className="mr-2 h-4 w-4" />
                  Improve Cover
                </a>
              </Button>
              <Button 
                onClick={handleDownload} 
                variant="outline" 
                className="flex-1 border-bookRed hover:bg-bookRed hover:text-white transition-all"
              >
                <Download className="mr-2 h-4 w-4" />
                Save Analysis
              </Button>
            </CardFooter>
          </Card>
        </div>
      </div>
      
      {imageUrl && (
        <ShareToGallerySection 
          imageUrl={imageUrl}
          rating={result.overallRating}
          critique={result.critique}
          genre={result.genrePrediction}
        />
      )}
    </div>
  );
};

export default ResultsSection;
